import type { GoogleMeasurementStatus } from './measurement';

/**
 * Whether a Google campaign's conversion count may be read as purchases (board ruling
 * GOOGLE-PURCHASES-AND-SMALL-BUDGETS, decision A). Pure, so the campaign page, the list and the
 * verdict all ask one function.
 *
 * Every condition must hold:
 * 1. `googleMeasurementStatus({ goal: 'sales' })` is `ready` (enabled, primary, PURCHASE, biddable,
 *    catalog read in the last 7 days).
 * 2. Every counted purchase goal is WEBSITE origin. `purchase_conversions` is one number per
 *    category, so a second origin under PURCHASE cannot be separated out and is not read.
 * 3. `purchase_conversions` is measured (not null) for every delivery day in the window, and the window
 *    has at least one delivery day: an empty window proves nothing.
 *
 * 4. The counted website tag is checked against the workspace's store domain (fifth proof, amendment
 *    to decision A). No stored data ties a conversion action to a domain (the catalog rows carry id, name,
 *    category, origin, status and primary only), so no reader exists and `storeChecked` stays false: the
 *    answer is `store_unchecked`. When a reader exists it passes `true` only on an exact host match,
 *    ignoring `www.`.
 *
 * Anything that cannot be proven refuses with its own reason, never a zero.
 */

export const GOOGLE_WEBSITE_ORIGIN = 'WEBSITE';

/** A day the campaign delivered, with the typed purchase count Mora stored for it (null: not measured). */
export interface GoogleTypedPurchaseDay {
  day: string;
  purchases: number | null;
}

export type GooglePurchasesNotReadReason =
  'no_purchase_conversion' | 'other_source' | 'unread' | 'not_reported' | 'store_unchecked';

export type GoogleReadsPurchases =
  { reads: true } | { reads: false; reason: GooglePurchasesNotReadReason };

/** Why the proof failed, or null when it passed (unions do not narrow with strict off). */
export function notReadReason(proof: GoogleReadsPurchases): GooglePurchasesNotReadReason | null {
  return proof.reads ? null : (proof as { reason: GooglePurchasesNotReadReason }).reason;
}

/** Shown on the Made for line, in the ruling's own words. */
export const GOOGLE_PURCHASE_COUNTED_LINE =
  'Google counts purchases from your website tag. Add a target cost per purchase to see how this campaign is doing.';

export function googlePurchaseTargetLine(amount: string): string {
  return `Target ${amount} or less per purchase, as reported by Google.`;
}

export const GOOGLE_PURCHASE_NOT_READ_LINES: Readonly<
  Record<GooglePurchasesNotReadReason, string>
> = {
  no_purchase_conversion:
    'No target: your Google Ads account has no purchase conversion set up, so there is nothing to count yet.',
  other_source:
    'No target: Google counts this sale from another source, and Mora reads website purchases only.',
  unread: 'Not checked. Mora has not read your Google conversion setup in the last 7 days.',
  not_reported: 'Google has not reported purchases for these days yet.',
  store_unchecked:
    'Google counts purchases from a website tag Mora has not checked against this store.',
};

/** The same reason as a plain sentence, for the line that says a count is not read as purchases. */
export function googlePurchaseNotReadNote(reason: GooglePurchasesNotReadReason): string {
  const line = GOOGLE_PURCHASE_NOT_READ_LINES[reason].replace(/^No target: /, '');
  return `${line.charAt(0).toUpperCase()}${line.slice(1)}`;
}

/**
 * The one origin every counted purchase goal shares, read from the catalog rows the status was built
 * from. `'MIXED'` when they differ and null when the status counts nothing.
 */
export function googlePurchaseOrigin(status: GoogleMeasurementStatus | null): string | null {
  if (!status || status.status !== 'ready') return null;
  const origins = new Set(status.counted.map((goal) => goal.origin));
  if (origins.size === 0) return null;
  return origins.size === 1 ? [...origins][0] : 'MIXED';
}

export function googleReadsPurchases(
  status: GoogleMeasurementStatus | null,
  origin: string | null,
  typedRows: readonly GoogleTypedPurchaseDay[],
  storeChecked = false,
): GoogleReadsPurchases {
  if (!status || status.status === 'unread' || status.status === 'stale_catalog') {
    return { reads: false, reason: 'unread' };
  }
  if (status.status === 'no_conversion_action') {
    return { reads: false, reason: 'no_purchase_conversion' };
  }
  if (origin !== GOOGLE_WEBSITE_ORIGIN) return { reads: false, reason: 'other_source' };
  if (typedRows.length === 0) return { reads: false, reason: 'not_reported' };
  for (const row of typedRows) {
    if (row.purchases === null || !Number.isFinite(row.purchases)) {
      return { reads: false, reason: 'not_reported' };
    }
  }
  if (!storeChecked) return { reads: false, reason: 'store_unchecked' };
  return { reads: true };
}

/**
 * The delivery days of one window joined to the stored typed rows. A delivery day with no stored row is
 * a null, so a gap in the sync refuses instead of reading as zero purchases. Days without delivery are
 * not asked for: Google writes no daily row for a day the campaign did not run.
 */
export function googleTypedPurchaseDays(
  points: readonly { day: string; spendMinor: number | null }[] | null | undefined,
  metricRows: readonly { metric_day: string; purchase_conversions: number | null }[],
): GoogleTypedPurchaseDay[] {
  const byDay = new Map<string, number | null>();
  for (const row of metricRows) {
    const known = byDay.get(row.metric_day);
    const next = row.purchase_conversions;
    byDay.set(row.metric_day, next === null ? (known ?? null) : (known ?? 0) + next);
  }
  return (points ?? [])
    .filter((point) => (point.spendMinor ?? 0) > 0)
    .map((point) => ({ day: point.day, purchases: byDay.get(point.day) ?? null }));
}

export function googlePurchaseTotal(rows: readonly GoogleTypedPurchaseDay[]): number | null {
  if (rows.length === 0) return null;
  let total = 0;
  for (const row of rows) {
    if (row.purchases === null) return null;
    total += row.purchases;
  }
  return Math.round(total * 10) / 10;
}

/**
 * The value the advertiser assigned to the purchases of one window (`purchase_conversions_value`), summed
 * over the same delivery days. Null when there is no delivery day or any of them has no stored value, so
 * a gap is never a zero. It is the value set on the conversion action, never revenue.
 */
export function googlePurchaseValueTotal(
  points: readonly { day: string; spendMinor: number | null }[] | null | undefined,
  metricRows: readonly { metric_day: string; purchase_conversions_value?: number | null }[],
): number | null {
  const delivered = (points ?? []).filter((point) => (point.spendMinor ?? 0) > 0);
  if (delivered.length === 0) return null;
  const byDay = new Map<string, number | null>();
  for (const row of metricRows) {
    const next = row.purchase_conversions_value ?? null;
    const known = byDay.get(row.metric_day);
    byDay.set(row.metric_day, next === null ? (known ?? null) : (known ?? 0) + next);
  }
  let total = 0;
  for (const point of delivered) {
    const value = byDay.get(point.day) ?? null;
    if (value === null) return null;
    total += value;
  }
  return Math.round(total * 100) / 100;
}
